'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';
import { Environment } from '@/env';

export const DataDogProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    // Only initialize if we have the required configuration
    if (!Environment.DataDog.applicationId || !Environment.DataDog.clientToken) {
      console.log('DataDog RUM not initialized: Missing application ID or client token');
      return;
    }

    // Initialize DataDog RUM
    datadogRum.init({
      applicationId: Environment.DataDog.applicationId,
      clientToken: Environment.DataDog.clientToken,
      site: Environment.DataDog.site as
        | 'datadoghq.com'
        | 'us3.datadoghq.com'
        | 'us5.datadoghq.com'
        | 'datadoghq.eu'
        | 'ddog-gov.com'
        | 'ap1.datadoghq.com',
      service: Environment.DataDog.service,
      env: Environment.DataDog.env,
      // Session configuration
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      // Track user interactions
      trackUserInteractions: true,
      // Track resources
      trackResources: true,
      // Track long tasks
      trackLongTasks: true,
      // Enable tracing
      enableExperimentalFeatures: ['clickmap'],
      // Default privacy level
      defaultPrivacyLevel: 'mask-user-input',
      // Performance configuration
      trackViewsManually: false,
      // Error tracking
      beforeSend: (event) => {
        // Filter out specific errors if needed
        if (event.type === 'error' && event.error?.message?.includes('Script error')) {
          return false;
        }
        return true;
      },
    });

    // Start RUM session
    datadogRum.startSessionReplayRecording();

    console.log(`DataDog RUM initialized for service: ${Environment.DataDog.service}`);
  }, []);

  return <>{children}</>;
};

// Utility functions for custom tracking
export const trackEvent = (event: string, metadata?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.addAction(event, {
      event,
      ...metadata,
    });
  }
};

export const trackCheckoutStep = (step: string, metadata?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.addAction(`checkout_${step}`, {
      step,
      ...metadata,
    });
  }
};

export const trackConversion = (orderId: string, amount: number, currency: string = 'USD') => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.addAction('purchase_completed', {
      orderId,
      amount,
      currency,
    });
  }
};

export const setUserContext = (userId?: string, email?: string, name?: string) => {
  if (typeof window !== 'undefined' && datadogRum) {
    datadogRum.setUser({
      id: userId,
      email,
      name,
    });
  }
};
